import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../styles/Home.css";
import heroBoardEn from "../assets/hero-board-182-en.jpg";
import heroBoardPl from "../assets/hero-board-182-pl.jpg";
import featureMissions from "../assets/feature-missions-182.jpg";
import featureFlashcards from "../assets/feature-flashcards-182.jpg";

const STORE_APPLE = "https://apps.apple.com/pl/app/dialecto/id6746904195";
const STORE_GOOGLE = "https://play.google.com/store/apps/details?id=com.dialectoapp&hl=en";

// SEO landing pages target the language their queries are searched in:
// EN for "learn european portuguese" / "learn norwegian", PL for "nauka norweskiego".
const MN_ALTERNATES = [
  { hrefLang: "en", href: "https://dialectoapp.com/mnemonics-app/" },
  { hrefLang: "pl", href: "https://dialectoapp.com/mnemotechniki/" },
  { hrefLang: "x-default", href: "https://dialectoapp.com/mnemonics-app/" },
];

const NO_ALTERNATES = [
  { hrefLang: "en", href: "https://dialectoapp.com/learn-norwegian/" },
  { hrefLang: "pl", href: "https://dialectoapp.com/nauka-norweskiego/" },
  { hrefLang: "x-default", href: "https://dialectoapp.com/learn-norwegian/" },
];

const PAGES = {
  pt: {
    slug: "learn-european-portuguese",
    title: "European Portuguese Learning App — Learn the Portuguese of Portugal | Dialecto",
    description:
      "Dialecto is a European Portuguese learning app — learn the Portuguese spoken in Portugal, not Brazil, from beginner level. Role-play real situations, remember words with smart mnemonics, and prepare for your trip. Free on iOS and Android.",
    eyebrow: "European Portuguese",
    h1: "Learn European Portuguese — Not Brazilian",
    lead:
      "Most apps teach Brazilian Portuguese. Dialecto is a learning app for the Portuguese actually spoken in Lisbon, Porto, and the Algarve — the pronunciation, the vocabulary, and the everyday phrases you'll really hear in Portugal. Built for beginners.",
    boardAlt: "Dialecto flashcard teaching the European Portuguese word 'a mala' with a mnemonic",
    whyTitle: "Why European Portuguese is different",
    why: [
      {
        title: "Different pronunciation",
        desc: "European Portuguese swallows unstressed vowels and sounds sharper than Brazilian. Train your ear on the real thing from day one instead of relearning later.",
      },
      {
        title: "Different everyday words",
        desc: "Comboio not trem, pequeno-almoço not café da manhã, telemóvel not celular. Dialecto teaches the words people in Portugal actually use.",
      },
      {
        title: "Formal and informal done right",
        desc: "Tu vs. você works differently in Portugal than in Brazil. Practice conversations that get the register right, so you don't sound off.",
      },
    ],
    howTitle: "How you'll learn Portuguese with Dialecto",
    how: [
      "Role-play real situations — order a bica in a Lisbon café, check in at a hotel in Porto, chat with locals at the market.",
      "Remember words for good with mnemonic flashcards — every word gets a memory trick and a picture, spaced repetition brings it back right before you'd forget.",
      "Prepare for your trip — tell Dialecto your travel plans and get practice missions built around the exact conversations you'll have in Portugal.",
      "Learn with Portuguese music — current hit playlists from Portugal, one tap away on Spotify, Apple Music, or YouTube.",
      "Start from zero — everything is built for beginner level, with guidance in English.",
    ],
    faq: [
      {
        q: "Does Dialecto teach European or Brazilian Portuguese?",
        a: "European Portuguese only — the variant spoken in Portugal. Vocabulary, phrases, and conversation scenarios all follow European usage, so what you learn matches what you'll hear in Lisbon or Porto.",
      },
      {
        q: "Can I start as a complete beginner?",
        a: "Yes. Dialecto is designed for beginners: you start with the most useful everyday words and phrases, and the app adapts the difficulty as you progress.",
      },
      {
        q: "Is Dialecto free?",
        a: "You can download Dialecto free on iOS and Android. Premium unlocks unlimited practice and starts with a 3-day free trial — cancel anytime.",
      },
      {
        q: "How fast can I learn enough for a trip to Portugal?",
        a: "With the trip missions feature, you practice exactly the conversations your trip will involve — most learners feel ready for café, restaurant, and small-talk situations within a few weeks of regular practice.",
      },
    ],
    ctaTitle: "Start speaking European Portuguese",
    ctaSubtitle: "Download Dialecto free and have your first Portuguese conversation today.",
  },
  no: {
    slug: "learn-norwegian",
    alternates: NO_ALTERNATES,
    title: "Norwegian Learning App for Beginners — Learn Bokmål | Dialecto",
    description:
      "Dialecto is a Norwegian learning app for beginners. Learn Bokmål through real conversations, remember words with smart mnemonics, and prepare for your trip or move to Norway. Free on iOS and Android.",
    eyebrow: "Norwegian Bokmål",
    h1: "Learn Norwegian for Beginners",
    lead:
      "Dialecto is a Norwegian learning app teaching Bokmål — the written standard used by the vast majority of Norwegians — through real conversations, clever memory tricks, and practice built around your own plans. No prior knowledge needed.",
    boardAlt: "Dialecto flashcard app teaching vocabulary with a mnemonic memory trick",
    whyTitle: "Why learn Norwegian with Dialecto",
    why: [
      {
        title: "Bokmål, the practical choice",
        desc: "Bokmål is the written form used by around 85% of Norwegians and what you'll see in Oslo, in newspapers, and at work. It's the standard courses and employers expect.",
      },
      {
        title: "Speak from day one",
        desc: "Norwegian grammar is friendlier than you think — no verb conjugation by person. Dialecto gets you into real conversations early instead of drilling tables.",
      },
      {
        title: "Words that stick",
        desc: "Every flashcard comes with a mnemonic and a picture, and spaced repetition resurfaces words right before you'd forget them — so vocabulary sticks for good.",
      },
    ],
    howTitle: "How you'll learn Norwegian with Dialecto",
    how: [
      "Role-play everyday situations — order coffee in Oslo, make small talk at work, handle a job interview in Norwegian.",
      "Remember words for good with mnemonic flashcards and spaced repetition.",
      "Prepare for your move or trip — tell Dialecto your plans and get practice missions built around the conversations you'll actually have in Norway.",
      "Learn with Norwegian music — current hit playlists from Norway, one tap away on Spotify, Apple Music, or YouTube.",
      "Start from zero — everything is built for beginner level, with guidance in English.",
    ],
    faq: [
      {
        q: "Does Dialecto teach Bokmål or Nynorsk?",
        a: "Bokmål — the written standard used by roughly 85% of Norwegians and the one nearly all learners start with. Once you know Bokmål, reading Nynorsk becomes much easier.",
      },
      {
        q: "Is Norwegian hard to learn for English speakers?",
        a: "Norwegian is considered one of the easiest languages for English speakers: related vocabulary, simple verb forms, and familiar word order. The challenge is practice — which is exactly what Dialecto gives you.",
      },
      {
        q: "Can I start as a complete beginner?",
        a: "Yes. Dialecto starts you with the most useful everyday words and phrases and adapts as you improve.",
      },
      {
        q: "Is Dialecto free?",
        a: "You can download Dialecto free on iOS and Android. Premium unlocks unlimited practice and starts with a 3-day free trial — cancel anytime.",
      },
    ],
    ctaTitle: "Start speaking Norwegian",
    ctaSubtitle: "Download Dialecto free and have your first Norwegian conversation today.",
  },
  "no-pl": {
    slug: "nauka-norweskiego",
    lang: "pl",
    ogLocale: "pl_PL",
    alternates: NO_ALTERNATES,
    ui: "pl",
    title: "Nauka norweskiego od podstaw — aplikacja do nauki norweskiego | Dialecto",
    description:
      "Ucz się norweskiego (bokmål) od podstaw. Scenki z życia, fiszki z mnemotechnikami i przygotowanie do wyjazdu lub pracy w Norwegii. Pobierz za darmo na iOS i Androida.",
    eyebrow: "Norweski bokmål",
    h1: "Nauka norweskiego od podstaw",
    lead:
      "Dialecto to aplikacja do nauki norweskiego bokmål — standardu, którym posługuje się zdecydowana większość Norwegów. Uczysz się przez prawdziwe rozmowy, sprytne mnemotechniki i praktykę zbudowaną wokół twoich planów. Praca w Norwegii, wyjazd czy rodzina — zaczynasz od zera i mówisz od pierwszego dnia.",
    boardAlt: "Fiszka Dialecto z mnemotechniką — zapamiętaj słówka na zawsze",
    whyTitle: "Dlaczego warto uczyć się norweskiego z Dialecto",
    why: [
      {
        title: "Bokmål — praktyczny wybór",
        desc: "Bokmål to standard pisany używany przez około 85% Norwegów — w Oslo, w gazetach i w pracy. To jego oczekują pracodawcy i kursy.",
      },
      {
        title: "Mów od pierwszego dnia",
        desc: "Norweska gramatyka jest prostsza, niż myślisz — czasowniki nie odmieniają się przez osoby. Dialecto od razu wciąga cię w prawdziwe rozmowy zamiast wkuwania tabelek.",
      },
      {
        title: "Słówka, które zostają",
        desc: "Każda fiszka ma mnemotechnikę i obrazek, a powtórki wracają dokładnie wtedy, gdy zaczynasz zapominać — słownictwo zostaje na dobre.",
      },
    ],
    howTitle: "Jak nauczysz się norweskiego z Dialecto",
    how: [
      "Odgrywaj codzienne scenki — zamów kawę w Oslo, pogadaj ze współpracownikami, przejdź rozmowę kwalifikacyjną po norwesku.",
      "Zapamiętuj słówka na zawsze dzięki fiszkom z mnemotechnikami i powtórkom rozłożonym w czasie.",
      "Przygotuj się do wyjazdu lub przeprowadzki — podaj Dialecto swoje plany, a dostaniesz misje treningowe wokół rozmów, które naprawdę cię czekają w Norwegii.",
      "Ucz się z norweską muzyką — aktualne playlisty hitów z Norwegii, jedno tapnięcie do Spotify, Apple Music lub YouTube.",
      "Zacznij od zera — wszystko zbudowane pod poziom początkujący.",
    ],
    faq: [
      {
        q: "Bokmål czy nynorsk — czego uczy Dialecto?",
        a: "Bokmål — standardu pisanego używanego przez około 85% Norwegów, od którego zaczynają praktycznie wszyscy uczący się. Znając bokmål, bez trudu przeczytasz też nynorsk.",
      },
      {
        q: "Czy norweski jest trudny dla Polaków?",
        a: "Norweski uchodzi za jeden z łatwiejszych języków germańskich: czasowniki nie odmieniają się przez osoby, szyk zdania jest przewidywalny, a wiele słów przypomina angielskie i niemieckie. Kluczem jest regularna praktyka mówienia — i dokładnie to daje Dialecto.",
      },
      {
        q: "Czy nauczę się norweskiego do pracy w Norwegii?",
        a: "Tak — scenki obejmują sytuacje zawodowe: rozmowę kwalifikacyjną, small talk ze współpracownikami, kontakt z klientem. Możesz też stworzyć własną scenkę pod swoją branżę.",
      },
      {
        q: "Czy mogę zacząć zupełnie od zera?",
        a: "Tak. Dialecto zaczyna od najpotrzebniejszych codziennych słów i zwrotów, a poziom trudności dostosowuje się do twoich postępów.",
      },
      {
        q: "Ile kosztuje Dialecto?",
        a: "Aplikację pobierzesz za darmo na iOS i Androida. Premium odblokowuje nieograniczoną praktykę i zaczyna się od 3-dniowego darmowego okresu próbnego — możesz anulować w każdej chwili.",
      },
    ],
    ctaTitle: "Zacznij mówić po norwesku",
    ctaSubtitle: "Pobierz Dialecto za darmo i odbądź pierwszą rozmowę po norwesku już dziś.",
  },
  mn: {
    slug: "mnemonics-app",
    alternates: MN_ALTERNATES,
    sideImg: "flashcards",
    title: "Mnemonics App for Language Learning — Remember Words Forever | Dialecto",
    description:
      "Dialecto is a mnemonics app for learning languages: every flashcard comes with a vivid memory trick and a picture, backed by spaced repetition. Stop forgetting vocabulary. Free on iOS and Android.",
    eyebrow: "Mnemonic method",
    h1: "The Mnemonics App for Language Learning",
    lead:
      "Forgetting words is normal — learning them twice is optional. In Dialecto every flashcard comes with a mnemonic: a vivid memory trick and a picture that link the word's sound to its meaning. Spaced repetition then brings it back right before you'd forget. Seven languages, including European Portuguese and Norwegian.",
    boardAlt: "Dialecto flashcard with a mnemonic memory trick — remember words forever",
    whyTitle: "Why mnemonics beat rote memorization",
    why: [
      {
        title: "Your brain loves stories",
        desc: "A word tied to a vivid image or mini-story leaves a far stronger memory trace than repeating it twenty times. Mnemonics turn abstract sounds into pictures you can't unsee.",
      },
      {
        title: "Ready for every word",
        desc: "You don't have to invent tricks yourself: Dialecto builds a tailored mnemonic with an illustration for any word you're learning — including ones you pick up in your own conversations.",
      },
      {
        title: "Spaced repetition seals it",
        desc: "Mnemonics get words into memory; spaced repetition keeps them there. Each card resurfaces right before you'd forget it, so reviews stay short and effective.",
      },
    ],
    howTitle: "How learning with mnemonics works in Dialecto",
    how: [
      "Meet a word in a real conversation — in role-play scenes, trip missions, or song lyrics.",
      "Tap it to make a flashcard — Dialecto creates a mnemonic and a picture linking the sound to the meaning.",
      "Review in short sessions — spaced repetition schedules every card at the perfect moment.",
      "Use the word back in conversation — recalling it under real conditions locks it in for good.",
      "Works across all seven languages — from European Portuguese to Norwegian.",
    ],
    faq: [
      {
        q: "What is a mnemonic, exactly?",
        a: "A memory trick that ties a new word to something you already know — an image, a story, a similar-sounding word. Example: Portuguese 'a mala' (suitcase) — picture your friend Mala dragging a giant suitcase through the airport. Silly on purpose: the sillier the image, the better it sticks.",
      },
      {
        q: "Do mnemonics really work for language learning?",
        a: "Yes — vivid associations and testing yourself at spaced intervals are two of the best-documented techniques in memory research. Dialecto combines both in every flashcard.",
      },
      {
        q: "Do I have to come up with the mnemonics myself?",
        a: "No. Dialecto creates a mnemonic with an illustration for every word automatically — you just review and let the method do the work.",
      },
      {
        q: "Which languages does it work with?",
        a: "All seven Dialecto languages: European Portuguese, Norwegian, English, German, Spanish, French, and Italian.",
      },
      {
        q: "Is Dialecto free?",
        a: "You can download Dialecto free on iOS and Android. Premium unlocks unlimited practice and starts with a 3-day free trial — cancel anytime.",
      },
    ],
    ctaTitle: "Stop forgetting vocabulary",
    ctaSubtitle: "Download Dialecto free and remember your first ten words with mnemonics today.",
  },
  "mn-pl": {
    slug: "mnemotechniki",
    lang: "pl",
    ogLocale: "pl_PL",
    alternates: MN_ALTERNATES,
    sideImg: "flashcards",
    title: "Mnemotechniki do nauki słówek — aplikacja, która pamięta za ciebie | Dialecto",
    description:
      "Dialecto to aplikacja z mnemotechnikami: każda fiszka ma obrazowy trik pamięciowy z ilustracją plus powtórki w odstępach. Przestań zapominać słówka. Za darmo na iOS i Androida.",
    eyebrow: "Metoda mnemotechniczna",
    h1: "Mnemotechniki: zapamiętaj słówka raz na zawsze",
    lead:
      "Zapominanie słówek jest normalne — uczenie się ich drugi raz już nie. W Dialecto każda fiszka dostaje mnemotechnikę: obrazowy trik pamięciowy z ilustracją, który łączy brzmienie słowa ze znaczeniem. A powtórki wracają dokładnie wtedy, gdy zaczynasz zapominać. Siedem języków, w tym norweski i portugalski europejski.",
    boardAlt: "Fiszka Dialecto z mnemotechniką — zapamiętaj słówka na zawsze",
    whyTitle: "Dlaczego mnemotechniki wygrywają z wkuwaniem",
    why: [
      {
        title: "Mózg kocha obrazy i historie",
        desc: "Słowo powiązane z wyrazistym obrazem albo mini-historią zostawia dużo silniejszy ślad pamięciowy niż powtórzone dwadzieścia razy. Mnemotechnika zamienia abstrakcyjne brzmienie w obraz, którego nie da się zapomnieć.",
      },
      {
        title: "Gotowa do każdego słówka",
        desc: "Nie musisz nic wymyślać: Dialecto tworzy dopasowaną mnemotechnikę z ilustracją do każdego słowa, które poznajesz — także tych z twoich własnych rozmów.",
      },
      {
        title: "Powtórki domykają sprawę",
        desc: "Mnemotechnika wprowadza słowo do pamięci, a powtórki w odstępach trzymają je tam na stałe. Fiszka wraca dokładnie przed momentem, w którym zacząłbyś zapominać.",
      },
    ],
    howTitle: "Jak działa nauka z mnemotechnikami w Dialecto",
    how: [
      "Poznajesz słowo w prawdziwej rozmowie — w scence, misji podróżnej albo tekście piosenki.",
      "Jedno tapnięcie tworzy fiszkę — Dialecto dokłada mnemotechnikę i obrazek łączący brzmienie ze znaczeniem.",
      "Powtarzasz w krótkich sesjach — powtórki w odstępach planują każdą fiszkę na idealny moment.",
      "Używasz słowa z powrotem w rozmowie — przypomnienie w realnych warunkach utrwala je na dobre.",
      "Działa we wszystkich siedmiu językach — od norweskiego po portugalski europejski.",
    ],
    faq: [
      {
        q: "Czym właściwie jest mnemotechnika?",
        a: "To trik pamięciowy, który łączy nowe słowo z czymś, co już znasz — obrazem, historyjką, podobnie brzmiącym wyrazem. Przykład: portugalskie 'a mala' (walizka) — wyobraź sobie koleżankę Malę ciągnącą przez lotnisko gigantyczną walizkę. Celowo absurdalne: im głupszy obraz, tym lepiej się trzyma.",
      },
      {
        q: "Czy mnemotechniki naprawdę działają?",
        a: "Tak — wyraziste skojarzenia i sprawdzanie się w rosnących odstępach czasu to dwie najlepiej udokumentowane techniki w badaniach nad pamięcią. Dialecto łączy obie w każdej fiszce.",
      },
      {
        q: "Czy muszę sam wymyślać mnemotechniki?",
        a: "Nie. Dialecto tworzy mnemotechnikę z ilustracją do każdego słowa automatycznie — ty tylko powtarzasz, a metoda robi swoje.",
      },
      {
        q: "Do jakich języków to działa?",
        a: "Do wszystkich siedmiu języków Dialecto: norweskiego, portugalskiego europejskiego, angielskiego, niemieckiego, hiszpańskiego, francuskiego i włoskiego.",
      },
      {
        q: "Ile kosztuje Dialecto?",
        a: "Aplikację pobierzesz za darmo na iOS i Androida. Premium odblokowuje nieograniczoną praktykę i zaczyna się od 3-dniowego darmowego okresu próbnego — możesz anulować w każdej chwili.",
      },
    ],
    ctaTitle: "Przestań zapominać słówka",
    ctaSubtitle: "Pobierz Dialecto za darmo i zapamiętaj dziś pierwsze dziesięć słówek z mnemotechnikami.",
  },
};

function useLandingSeo(page) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = page.title;

    // Trailing slash: Apache redirects the slash-less directory URL with a 301,
    // so the canonical must be the final (slashed) form.
    const url = `https://dialectoapp.com/${page.slug}/`;
    const managed = [];

    const setMeta = (selector, create, content) => {
      let el = document.head.querySelector(selector);
      let created = false;
      if (!el) {
        el = create();
        document.head.appendChild(el);
        created = true;
      }
      const prev = { el, created, content: el.getAttribute("content"), href: el.getAttribute("href") };
      managed.push(prev);
      if (content.href) el.setAttribute("href", content.href);
      if (content.content) el.setAttribute("content", content.content);
    };

    const meta = (name) => () => {
      const m = document.createElement("meta");
      m.setAttribute("name", name);
      return m;
    };
    const prop = (p) => () => {
      const m = document.createElement("meta");
      m.setAttribute("property", p);
      return m;
    };

    setMeta('meta[name="description"]', meta("description"), { content: page.description });
    setMeta('link[rel="canonical"]', () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      return l;
    }, { href: url });
    setMeta('meta[property="og:title"]', prop("og:title"), { content: page.title });
    setMeta('meta[property="og:description"]', prop("og:description"), { content: page.description });
    setMeta('meta[property="og:url"]', prop("og:url"), { content: url });
    if (page.ogLocale) {
      setMeta('meta[property="og:locale"]', prop("og:locale"), { content: page.ogLocale });
    }

    const prevLang = document.documentElement.getAttribute("lang");
    document.documentElement.setAttribute("lang", page.lang || "en");

    const alternateEls = (page.alternates || []).map((alt) => {
      const l = document.createElement("link");
      l.setAttribute("rel", "alternate");
      l.setAttribute("hreflang", alt.hrefLang);
      l.setAttribute("href", alt.href);
      document.head.appendChild(l);
      return l;
    });

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "landing-faq-ld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(ld);

    const appLd = document.createElement("script");
    appLd.type = "application/ld+json";
    appLd.id = "landing-app-ld";
    appLd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "MobileApplication",
      name: "Dialecto",
      operatingSystem: "iOS, Android",
      applicationCategory: "EducationalApplication",
      description: page.description,
      url,
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    });
    document.head.appendChild(appLd);

    return () => {
      document.title = prevTitle;
      if (prevLang) document.documentElement.setAttribute("lang", prevLang);
      alternateEls.forEach((el) => el.remove());
      ld.remove();
      appLd.remove();
      managed.forEach(({ el, created, content, href }) => {
        if (created) {
          el.remove();
        } else {
          if (content !== null) el.setAttribute("content", content);
          if (href !== null) el.setAttribute("href", href);
        }
      });
    };
  }, [page]);
}

const AppleBadge = () => (
  <svg width="20" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);
const GoogleBadge = () => (
  <svg width="20" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zM14.892 13.1l2.302 2.302-12.41 7.105 10.108-9.407zM18.288 8.61l2.832 1.622a1 1 0 0 1 0 1.736l-2.832 1.622-2.49-2.49 2.49-2.49zM4.784 1.407l12.41 7.105-2.302 2.302L4.784 1.407z" />
  </svg>
);

const StoreButtons = () => (
  <div className="hero-cta-group landing-cta-group">
    <a href={STORE_APPLE} target="_blank" rel="noopener noreferrer" className="store-button apple" aria-label="Download on App Store">
      <AppleBadge />
      <span className="store-button-text">
        <span className="store-button-eyebrow">Download on the</span>
        <span className="store-button-name">App Store</span>
      </span>
    </a>
    <a href={STORE_GOOGLE} target="_blank" rel="noopener noreferrer" className="store-button google" aria-label="Get it on Google Play">
      <GoogleBadge />
      <span className="store-button-text">
        <span className="store-button-eyebrow">Get it on</span>
        <span className="store-button-name">Google Play</span>
      </span>
    </a>
  </div>
);

function LearnLanding({ page: pageKey }) {
  const page = PAGES[pageKey];
  const boardImg = page.lang === "pl" ? heroBoardPl : heroBoardEn;
  const faqTitle = page.lang === "pl" ? "Częste pytania" : "Frequently asked questions";
  const homeLinkText = page.lang === "pl" ? "Zobacz wszystkie funkcje Dialecto →" : "See everything Dialecto offers →";
  const ctaNote = page.lang === "pl"
    ? "Darmowe pobranie • 3-dniowy okres próbny Premium • Anuluj w każdej chwili"
    : "Free download • 3-day Premium trial • Cancel anytime";
  useLandingSeo(page);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pageKey]);

  // Landing content is fixed per URL (each targets queries in one language),
  // so a header language switch navigates to the hreflang sibling instead of
  // re-rendering in place. Skips the initial mount: entering the page must
  // never redirect, only an explicit switch does.
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const lng = (i18n.language || "en").split("-")[0];
    if (lng === (page.lang || "en")) return;
    const alternates = page.alternates || [];
    const target =
      alternates.find((a) => a.hrefLang === lng) ||
      alternates.find((a) => a.hrefLang === "en");
    if (!target) return;
    const path = new URL(target.href).pathname.replace(/\/$/, "");
    if (path !== window.location.pathname.replace(/\/$/, "")) navigate(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return (
    <div className="landing">
      <section className="hero-section landing-hero">
        <div className="hero-background">
          <div className="hero-gradient-orb hero-orb-1"></div>
          <div className="hero-gradient-orb hero-orb-2"></div>
        </div>
        <div className="hero-content">
          <div className="hero-text">
            <span className="section-badge">{page.eyebrow}</span>
            <h1 className="hero-title landing-h1">{page.h1}</h1>
            <p className="hero-subtitle">{page.lead}</p>
            <StoreButtons />
          </div>
          <div className="hero-visual">
            <div className="hero-board">
              <img src={boardImg} alt={page.boardAlt} className="hero-board-img" />
              <div className="phone-glow"></div>
            </div>
          </div>
        </div>
      </section>

      <section className="features-overview landing-section">
        <div className="section-header">
          <h2 className="section-title">{page.whyTitle}</h2>
        </div>
        <div className="features-grid landing-why-grid">
          {page.why.map((w, i) => (
            <div key={i} className="feature-card">
              <h3 className="feature-title">{w.title}</h3>
              <p className="feature-desc">{w.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="landing-section landing-how">
        <div className="landing-how-inner">
          <div className="landing-how-text">
            <h2 className="section-title">{page.howTitle}</h2>
            <ul className="landing-how-list">
              {page.how.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
            <p className="landing-home-link">
              <Link to="/">{homeLinkText}</Link>
            </p>
          </div>
          <div className="landing-how-visual">
            <img
              src={page.sideImg === "flashcards" ? featureFlashcards : featureMissions}
              alt={
                page.sideImg === "flashcards"
                  ? "Dialecto flashcard with a mnemonic and picture"
                  : "Dialecto trip missions — practice conversations for your travel plans"
              }
              className="landing-side-img"
            />
          </div>
        </div>
      </section>

      <section className="faq-section landing-section">
        <div className="section-header">
          <h2 className="section-title">{faqTitle}</h2>
        </div>
        <div className="faq-container landing-faq">
          {page.faq.map((f, i) => (
            <div key={i} className="landing-faq-item">
              <h3>{f.q}</h3>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="final-cta">
        <div className="final-cta-content">
          <h2>{page.ctaTitle}</h2>
          <p>{page.ctaSubtitle}</p>
          <StoreButtons />
          <p className="cta-note">{ctaNote}</p>
        </div>
      </section>
    </div>
  );
}

export default LearnLanding;
